import { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { StarRating } from 'react-flexible-star-rating';
import { PageTitle, Richtext, Section, useCacheQuery, useCacheRecord } from 'gossamer-front-end';
import { FeedbackForm, FeedbackFormRow, FeedbackFormQuestion, FeedbackFormQuestionRow } from 'cb-model';

type Props = {
  eventId: number;
  feedbackFormId: number | null;
  title?: string;
  onSubmit: (data: any) => Promise<void>;
};

const DEFAULT_FORM: FeedbackFormRow = {
  id: 0,
  title: 'Default Form',
  status: 'ACTIVE',
  institution_id: 0,
  internal_external_mode: 'internal',
  external_form_url: '',
};

const DEFAULT_FORM_QUESTIONS: FeedbackFormQuestionRow[] = [
  {
    id: 1,
    seq: 10,
    question_type: 'star_rating',
    title: 'Content',
    subtitle: 'was the material pitched at the right level, accurate and informative?',
    is_mandatory: true,
    standard_field: 'content',
    feedback_form_id: 0,
  },
  {
    id: 2,
    seq: 20,
    question_type: 'star_rating',
    title: 'Delivery',
    subtitle: 'was the material presented well? Were slides/videos/handouts useful?',
    is_mandatory: true,
    standard_field: 'delivery',
    feedback_form_id: 0,
  },
  {
    id: 3,
    seq: 30,
    question_type: 'star_rating',
    title: 'Booking, organisation and admin',
    subtitle: 'was attending this event a smooth experience?',
    is_mandatory: true,
    standard_field: 'admin',
    feedback_form_id: 0,
  },
  {
    id: 4,
    seq: 90,
    question_type: 'star_rating',
    title: 'Overall score',
    subtitle: '',
    is_mandatory: true,
    standard_field: 'overall',
    feedback_form_id: 0,
  },
  {
    id: 5,
    seq: 95,
    question_type: 'long_text',
    title: 'Additional comments',
    subtitle: '',
    is_mandatory: false,
    standard_field: '',
    feedback_form_id: 0,
  },
];

interface ExternalFeedbackFormLinkProps {
  feedbackFormId: number;
  eventId: number;
  externalFormUrl: string;
  onSubmit: (data: any) => Promise<void>;
}

const ExternalFeedbackFormLink = (props: ExternalFeedbackFormLinkProps) => {
  const handleClick = () => {
    props
      .onSubmit({
        eventId: props.eventId,
        feedbackFormId: props.feedbackFormId,
        responses: null,
      })
      .then(() => window.open(props.externalFormUrl || 'https://careerbook.co.uk', '_blank'))
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <div>
      <a
        onClick={handleClick}
        style={{
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Please click here to provide feedback using the provider's preferred form
      </a>
      <br />
      <br />
      <div style={{ textAlign: 'center' }}>Then return here to download your certificate</div>
      <br />
      <br />
      <br />
      <br />
    </div>
  );
};

export const CaptureEventFeedbackForm = ({ eventId, feedbackFormId, title, onSubmit }: Props) => {
  const feedbackFormRecord = useCacheRecord(FeedbackForm, String(feedbackFormId), !feedbackFormId);
  const feedbackQuestionsQuery = useCacheQuery(
    FeedbackFormQuestion,
    {
      where: {
        and: [{ fieldId: 'feedback_form_id', operator: 'eq', value: feedbackFormId }],
      },
      limit: 100, // max number of questions per form
    },
    !feedbackFormId,
  );

  const feedbackForm = !!feedbackFormId ? feedbackFormRecord.data : DEFAULT_FORM;
  const feedbackQuestions = !!feedbackFormId ? feedbackQuestionsQuery.data : DEFAULT_FORM_QUESTIONS;

  return (
    <>
      {!feedbackForm && <div>Loading...</div>}
      {feedbackForm && feedbackForm.internal_external_mode === 'external' && (
        <ExternalFeedbackFormLink
          eventId={eventId}
          externalFormUrl={feedbackFormRecord.data?.external_form_url || ''}
          feedbackFormId={feedbackFormId || 0}
          onSubmit={onSubmit}
        />
      )}
      {feedbackForm && feedbackQuestions && feedbackForm.internal_external_mode !== 'external' && (
        <FeedbackFormInner
          eventId={eventId}
          feedbackForm={feedbackForm}
          feedbackQuestions={feedbackQuestions}
          title={title}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};

type FeedbackFormInnerProps = Omit<Props, 'feedbackFormId'> & {
  feedbackForm: FeedbackFormRow;
  feedbackQuestions: FeedbackFormQuestionRow[];
};

function FeedbackFormInner(props: FeedbackFormInnerProps) {
  const [data, setData] = useState<Record<string, string | number>>({});
  const setRating = (fieldId: string) => (newRating: number) => setData({ ...data, [fieldId]: newRating });
  const sortedQuestions = useMemo(() => {
    const copy = props.feedbackQuestions.slice();
    copy.sort((a, b) => a.seq - b.seq);
    return copy;
  }, [props.feedbackQuestions]);
  const readyToSubmit = props.feedbackQuestions.reduce(
    (prev, curr) => prev && (!curr.is_mandatory || !!data['q' + curr.id]),
    true,
  );

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          const payload: any = {
            eventId: props.eventId,
            feedbackFormId: props.feedbackForm.id,
            responses: JSON.stringify(data),
          };
          sortedQuestions.forEach((question) => {
            if (question.standard_field) {
              payload[question.standard_field + 'Score'] = data['q' + question.id];
            }
          });
          props
            .onSubmit(payload)
            // .then(() => loadingButtonRef.current?.setIsLoading(false))
            .catch((e) => {
              console.error(e);
              // loadingButtonRef.current?.setIsLoading(false);
            });
        }}
        noValidate
      >
        <PageTitle title={props.title} />
        <p>
          We value your feedback. Please give ratings for this event against the criteria below and add any further
          comments.
        </p>
        <Section>
          {sortedQuestions.map((question) => (
            <div key={'q' + question.id}>
              <b>{question.title}</b>: {question.subtitle}
              {question.question_type === 'star_rating' && (
                <StarRating dimension={10} onRatingChange={setRating('q' + question.id)} />
              )}
              {question.question_type === 'yes_no' && (
                <div>
                  <span>
                    <input
                      type="radio"
                      id={'q' + question.id + '.yes'}
                      name={'q' + question.id}
                      onChange={() => setData({ ...data, ['q' + question.id]: 'yes' })}
                    />
                    <label htmlFor={'q' + question.id + '.yes'} style={{ marginLeft: 5 }}>
                      yes
                    </label>
                  </span>
                  <span style={{ marginLeft: 20 }}>
                    <input
                      type="radio"
                      id={'q' + question.id + '.no'}
                      name={'q' + question.id}
                      onChange={() => setData({ ...data, ['q' + question.id]: 'no' })}
                    />
                    <label htmlFor={'q' + question.id + '.no'} style={{ marginLeft: 5 }}>
                      no
                    </label>
                  </span>
                </div>
              )}
              {question.question_type === 'short_text' && (
                <>
                  <input
                    id={'q' + question.id}
                    className="section-form-input"
                    placeholder={question.subtitle}
                    value={data['q' + question.id] || ''}
                    onChange={(event) => {
                      setData({ ...data, ['q' + question.id]: event.target.value });
                    }}
                  />
                  <div>
                    Please do not include personal, confidential, or sensitive information (e.g. health details) in your
                    response.
                  </div>
                </>
              )}
              {question.question_type === 'long_text' && (
                <>
                  <Richtext
                    handleChange={(newValue) => {
                      setData({ ...data, ['q' + question.id]: newValue });
                    }}
                    mandatory={question.is_mandatory}
                    validity={!!data['q' + question.id] || !question.is_mandatory}
                    value={(data['q' + question.id] as string) || ''}
                  />
                  <div>
                    Please do not include personal, confidential, or sensitive information (e.g. health details) in your
                    response.
                  </div>
                </>
              )}
              <br />
            </div>
          ))}
          <div>
            <Button type="submit" disabled={!readyToSubmit}>
              Submit
            </Button>
          </div>
        </Section>
      </form>
    </>
  );
}
